/**
 * Schritt 4 "Einbinden" (33c) — Status-Karte + Einbau-Code + Erlaubte Websites. Der Einbau-Code-
 * Dialog aus dem alten Drawer (widgets-tab.tsx, "Einbinden"-Modal) entfaellt komplett und wandert
 * hierher (Bauplan). Loeschen sitzt hier im Kopf dieses Schritts (Entscheidung: siehe Rueckmeldung
 * der Bau-Session — die zweistufige Live-Sperre bleibt server- UND UI-seitig erhalten).
 *
 * "Powered by MAESTRO" (W4.4/E-W7) wohnt jetzt hier statt im alten Anlage-Drawer: der Bauplan
 * nennt fuer diesen Schritt keinen festen Platz, der Schalter beeinflusst aber genau das, was
 * dieser Schritt zeigt (wie das Widget beim Einbinden erscheint) - deshalb hier statt in einem der
 * anderen drei Schritte platziert.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ErrorNote, Icon, Modal, Checkbox, Textarea } from "../../../ui/components";
import { menueLinkBeispiel, snippetFuer, STATUS_LABEL, type Widget } from "../../widgets-tab";
import { formatDateTime } from "../../../ui/format";
import { useApi } from "../../../hooks/useApi";
import type { WidgetEditor } from "../useWidgetEditorForm";
import WirkungKarte from "../WirkungKarte";
import { ABLAUF_SPRACHEN, ABLAUF_SPRACHE_LABEL, leseAblauf, type AblaufSprache } from "../ablauf-modell";
import { EINBAU_TIPPS } from "../beratung";

/** Grobe Seitenskizze (Mockup „Einbau auf Ihrer Website“): Blöcke mit relativer Höhe, das Formular hervorgehoben. */
function Skizze({ titel, unter, bloecke }: { titel: string; unter: string; bloecke: { text: string; hoehe: number; form?: boolean }[] }) {
  return (
    <figure className="min-w-0 flex-1">
      <figcaption className="mb-1 font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">
        {titel} <span className="font-normal text-on-surface-variant">· {unter}</span>
      </figcaption>
      <div className="flex h-[220px] flex-col gap-1 rounded-xl border border-outline-variant/30 p-1.5 dark:border-white/10" role="img" aria-label={`${titel}: ${unter}`}>
        {bloecke.map((b) => (
          <div
            key={b.text}
            style={{ flex: b.hoehe }}
            className={`grid min-h-0 place-items-center overflow-hidden rounded px-1 text-center font-label-md text-[10px] leading-tight ${
              b.form ? "bg-primary/15 font-semibold text-primary" : "bg-surface-container-high text-on-surface-variant"
            }`}
          >
            {b.text}
          </div>
        ))}
      </div>
    </figure>
  );
}

export default function EmbedStep({ editor }: { editor: WidgetEditor }) {
  const { widget, form } = editor;
  const [kopiert, setKopiert] = useState(false);
  const [codeSprache, setCodeSprache] = useState<AblaufSprache | null>(null);
  // Punkt 134: Stil-Block nur per Haken, aus den gespeicherten Werten aus „Aussehen“. Der Haken selbst wird nicht gespeichert.
  const [mitStil, setMitStil] = useState(false);
  const stil = mitStil ? form.aussehen : undefined;
  // Die Geschwister-Widgets (eine Sprache = ein Widget), nur mit Kachel-Ablauf gebraucht.
  const alleQ = useApi<Widget[]>(form.ablaufAn ? "/api/widgets" : null);
  const [loeschOffen, setLoeschOffen] = useState(false);
  const navigate = useNavigate();
  if (!widget) return null;

  const eigeneSprache = form.ablauf.sprache;
  const aktiveSprache = codeSprache ?? eigeneSprache;
  // Welches Widget gehoert zu welcher Sprache? Dieses hier zaehlt fuer seine eigene, sonst das erste passende.
  const widgetFuer = (l: AblaufSprache): string | null => {
    if (l === eigeneSprache) return widget.id;
    const liste = Array.isArray(alleQ.data) ? alleQ.data : [];
    const treffer = liste.find((w) => w.id !== widget.id && w.kind === "form" && leseAblauf(w.draftConfig)?.sprache === l);
    return treffer?.id ?? null;
  };
  const codeId = form.ablaufAn ? widgetFuer(aktiveSprache) : widget.id;
  const codeText = codeId ? snippetFuer(codeId, form.ablaufAn ? { anker: true, sprache: aktiveSprache, stil } : { stil }) : null;

  const stilHaken = (
    <div>
      <label className="flex items-center gap-2">
        <Checkbox checked={mitStil} onChange={(e) => setMitStil(e.target.checked)} />
        <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Stil auf der Website festlegen</span>
      </label>
      <p className="mt-1 font-label-md text-label-md text-on-surface-variant" data-testid="stil-hinweis">
        Überschreibt die Einstellungen aus „Aussehen“: Ein Stil-Block auf der Website gewinnt immer. Ohne Haken braucht deine Website keinen
        Stil-Block, das Widget holt sich das Aussehen aus MAESTRO.
      </p>
    </div>
  );

  async function kopieren() {
    if (!codeText) return;
    try {
      await navigator.clipboard.writeText(codeText);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      // Kein Clipboard (z. B. unsicherer Kontext): der Text bleibt markier- und kopierbar.
    }
  }

  async function loeschen() {
    const ok = await editor.remove();
    if (ok) navigate("/einstellungen?tab=widgets");
    else setLoeschOffen(false);
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-2 p-4">
        <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Status</p>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {STATUS_LABEL[widget.status]}
          {" · "}
          {widget.publishedAt ? `Version ${widget.version} · publiziert ${formatDateTime(widget.publishedAt)}` : "Noch nie publiziert"}
        </p>
        {(widget.publishedAt == null || widget.status !== "live" || widget.allowedDomains.length === 0) && (
          <div className="rounded-xl bg-tertiary-container/60 p-3 font-body-sm text-body-sm text-on-tertiary-container">
            <p className="font-semibold">Damit das Widget auf Ihrer Website erscheint:</p>
            <ul className="mt-1 list-disc pl-5">
              {widget.publishedAt == null && <li>Widget publizieren (es wurde noch nie publiziert).</li>}
              {widget.publishedAt != null && widget.status !== "live" && <li>Widget fortsetzen (es ist pausiert).</li>}
              {widget.allowedDomains.length === 0 && <li>Ihre Website-Domain unten eintragen — ohne Eintrag liefert das Widget öffentlich nichts aus.</li>}
            </ul>
          </div>
        )}
      </Card>

      {form.ablaufAn && <WirkungKarte widgetId={widget.id} />}

      {form.ablaufAn ? (
        <Card className="space-y-4 p-4" data-testid="einbau-beratung">
          <div>
            <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Einbau auf Ihrer Website</p>
            <p className="mt-1 font-label-md text-label-md text-on-surface-variant">
              MAESTRO erzeugt den HTML-Code. Genau dieser Code kommt unverändert auf die Seite. Wo Sie ihn einsetzen, entscheidet mit, wie viele
              Anfragen ankommen.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Skizze
              titel="Zu vermeiden"
              unter="Formular ganz unten"
              bloecke={[
                { text: "Einstieg + 2 Knöpfe", hoehe: 2 },
                { text: "Vorteile", hoehe: 1 },
                { text: "Text, Zielgruppen", hoehe: 2 },
                { text: "Menüs", hoehe: 3 },
                { text: "Anfahrt, FAQ", hoehe: 3 },
                { text: "Formular", hoehe: 2, form: true },
                { text: "Kontakt, Links", hoehe: 1 },
              ]}
            />
            <Skizze
              titel="Empfohlen"
              unter="Formular im ersten Drittel"
              bloecke={[
                { text: "Einstieg · „Gruppe anfragen“ · „Menüs ansehen“", hoehe: 2 },
                { text: "Formular, Schritt 1 ganz sichtbar", hoehe: 4, form: true },
                { text: "Gruppenmenüs mit „Anfragen“", hoehe: 2 },
                { text: "Anfahrt Bus", hoehe: 1 },
                { text: "Bewertungen", hoehe: 1 },
                { text: "FAQ, geschlossen", hoehe: 2 },
                { text: "Kontakt, Links", hoehe: 1 },
              ]}
            />
          </div>
          <ul className="space-y-2" data-testid="einbau-tipps">
            {EINBAU_TIPPS.map((t) => (
              <li key={t.key} className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
                <b>{t.titel}</b> <span className="text-on-surface-variant">{t.grund}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <b className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">HTML-Ausgabe für</b>
            <div role="group" aria-label="Sprache des Codes" className="flex flex-wrap gap-2">
              {ABLAUF_SPRACHEN.map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={aktiveSprache === l}
                  className={`rounded-full border px-3 py-1 font-label-md text-label-md ${
                    aktiveSprache === l ? "border-primary bg-primary/10 font-semibold text-primary" : "border-outline-variant/40 text-on-surface-variant"
                  }`}
                  onClick={() => setCodeSprache(l)}
                >
                  {ABLAUF_SPRACHE_LABEL[l]}
                </button>
              ))}
            </div>
          </div>
          {stilHaken}
          {codeText ? (
            <>
              <pre className="overflow-x-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 font-mono text-[12px] leading-relaxed text-on-surface dark:border-white/10 dark:bg-white/[0.03] dark:text-inverse-on-surface" data-testid="html-ausgabe">
                {codeText}
              </pre>
              <Button type="button" variant="secondary" onClick={() => void kopieren()}>
                <Icon name={kopiert ? "check" : "content_copy"} className="text-[16px]" /> {kopiert ? "Kopiert!" : "Code kopieren"}
              </Button>
            </>
          ) : (
            <p className="rounded-xl bg-tertiary-container/60 p-3 font-body-sm text-body-sm text-on-tertiary-container" data-testid="html-ausgabe-fehlt">
              Für {ABLAUF_SPRACHE_LABEL[aktiveSprache]} gibt es noch kein eigenes Widget. Legen Sie unter Einstellungen › Widgets ein weiteres Widget an,
              stellen Sie im Schritt „Ablauf“ die Sprache {ABLAUF_SPRACHE_LABEL[aktiveSprache]} ein, und der Code erscheint hier.
            </p>
          )}
          <div>
            <p className="font-label-md text-label-md font-semibold text-on-surface dark:text-inverse-on-surface">Beispiel: Menükarte mit „Anfragen“</p>
            <pre className="mt-1 overflow-x-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 font-mono text-[12px] text-on-surface dark:border-white/10 dark:bg-white/[0.03] dark:text-inverse-on-surface" data-testid="menue-beispiel">
              {menueLinkBeispiel(form.ablauf.menues[0]?.schluessel ?? "m1")}
            </pre>
            <p className="mt-1 font-label-md text-label-md text-on-surface-variant">
              Der Schlüssel nennt das Menü aus dem Schritt „Ablauf“. Preise und Namen kommen nie von der Seite, sondern aus Ihrem Katalog.
            </p>
          </div>
          <p className="font-label-md text-label-md text-on-surface-variant">
            Je Sprache eine eigene Seite mit dem eigenen Widget.
          </p>
        </Card>
      ) : (
        <Card className="space-y-3 p-4">
          <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Einbau-Code</p>
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            Fügen Sie diese zwei Zeilen an der Stelle Ihrer Website ein, an der das Widget erscheinen soll:
          </p>
          <pre className="overflow-x-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 font-mono text-[12px] leading-relaxed text-on-surface dark:border-white/10 dark:bg-white/[0.03] dark:text-inverse-on-surface">
            {codeText}
          </pre>
          {stilHaken}
          <Button type="button" variant="secondary" onClick={() => void kopieren()}>
            <Icon name={kopiert ? "check" : "content_copy"} className="text-[16px]" /> {kopiert ? "Kopiert!" : "Snippet kopieren"}
          </Button>
        </Card>
      )}

      <Card className="space-y-2 p-4">
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Erlaubte Domains</span>
          <Textarea
            rows={3}
            className="font-mono text-[12.5px]"
            placeholder={"storia.de\nwww.storia.de"}
            value={form.domainsText}
            onChange={(e) => editor.updateForm((f) => ({ ...f, domainsText: e.target.value }))}
          />
          <span className="mt-1 block font-label-md text-label-md text-on-surface-variant">
            Eine Domain je Zeile (max. 20), Subdomains zählen mit. Nur diese Websites dürfen das Widget einbinden — ohne
            Eintrag liefert es öffentlich nichts aus.
          </span>
        </label>
      </Card>

      <Card className="space-y-2 p-4">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={form.poweredBy}
            onChange={(e) => editor.updateForm((f) => ({ ...f, poweredBy: e.target.checked }))}
          />
          <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">„Powered by MAESTRO“ anzeigen</span>
        </label>
        <p className="font-label-md text-label-md text-on-surface-variant">
          Eine kleine, blasse Zeile unter dem Widget auf Ihrer Website. Sie können sie hier abschalten — die Herkunftsangabe
          zu Google-Bewertungen ist davon nicht betroffen, die bleibt immer stehen (Auflage von Google).
        </p>
      </Card>

      {widget.status !== "live" && (
        <Card className="space-y-2 p-4">
          <p className="font-body-sm text-body-sm font-semibold text-error">Widget löschen</p>
          <p className="font-label-md text-label-md text-on-surface-variant">
            Löscht das Widget endgültig aus MAESTRO — das Snippet auf Ihrer Website liefert dann nichts mehr aus.
          </p>
          <Button type="button" variant="secondary" className="!text-error" onClick={() => setLoeschOffen(true)}>
            <Icon name="delete" className="text-[16px]" /> Löschen
          </Button>
        </Card>
      )}

      <Modal
        open={loeschOffen}
        onClose={() => !editor.actionBusy && setLoeschOffen(false)}
        width="480px"
        gefahr
        title={<>„{widget.name}“ löschen?</>}
        footer={
          <>
            <Button type="button" variant="secondary" disabled={editor.actionBusy} onClick={() => setLoeschOffen(false)}>
              Abbrechen
            </Button>
            <Button type="button" variant="danger" disabled={editor.actionBusy} onClick={() => void loeschen()}>
              <Icon name="delete_forever" className="text-[16px]" /> {editor.actionBusy ? "Löscht …" : "Endgültig löschen"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            Das Widget wird endgültig aus MAESTRO entfernt und liefert öffentlich nichts mehr aus.
            {widget.publishedAt != null
              ? " Ein noch auf Ihrer Website eingebundenes Snippet bleibt dort stehen, zeigt aber nichts mehr an — entfernen Sie es dort bei Gelegenheit."
              : ""}{" "}
            Das lässt sich nicht rückgängig machen.
          </p>
          {editor.actionError && <ErrorNote message={editor.actionError} />}
        </div>
      </Modal>
    </div>
  );
}
